import { View, ScrollView, Platform, FlatList, Animated, Easing, ViewStyle, StyleSheet } from 'react-native';
import { Pressable } from 'react-native';
import React, { useState, useEffect, useRef } from 'react';
import { Text } from '@constants/Themed';
import WidgetList from './WidgetList';
import styles from '@stylesheet';
import LottieView from '@utils/lottieAnimation';
import { changeWidget } from '@reduxLocal/reducers/widgetReducer';
import { connect, useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { LinearGradient } from 'expo-linear-gradient';
import { isLandscapeSync, isTablet } from 'react-native-device-info';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useDeviceType } from '@hooks/useDeviceType';
// Using FlatList for better compatibility - FlashList types seem incorrect

type WidgetSelectorProps = {
  activeWidget: any,
  userWidgetList: any,
  vertical: boolean,
  containerStyle?: ViewStyle
}

// Hero-inspired styling matching Hero.tsx aesthetic
const heroStyles = StyleSheet.create({
  container: {
    position: 'relative',
    borderRadius: Platform.OS === 'web' ? 0 : 0,
    overflow: 'visible',
    ...(Platform.OS === 'web' && {
      backgroundColor: 'transparent',
    }),
  },
  backgroundGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  containerFloatingBlur: {
    position: 'absolute',
    width: 200,
    height: 200,
    borderRadius: 100,
    ...(Platform.OS === 'web' && {
      filter: 'blur(60px)',
    }),
    zIndex: 1,
  },
  listContent: {
    position: 'relative',
    zIndex: 2,
  },
  widgetItemContainer: {
    position: 'relative',
  },
  widgetPressable: {
    ...(Platform.OS === 'web' && {
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    }),
  },
  glassCard: {
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
    elevation: 8,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
    }),
  },
  activeGlassCard: {
    ...(Platform.OS === 'web' && {
    }),
  },
  gradientBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  floatingBlur: {
    position: 'absolute',
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: Platform.OS === 'web' ? 'rgba(255, 165, 0, 0.15)' : 'transparent',
    ...(Platform.OS === 'web' && {
      filter: 'blur(30px)',
    }),
    zIndex: 1,
  },
  blurOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Platform.OS === 'web' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
    }),
    zIndex: 2,
  },
  contentContainer: {
    ...(Platform.OS === 'web' && {
      backgroundColor: 'transparent',
    }),
  },
  widgetLabel: {
    ...(Platform.OS === 'web' && {
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    }),
  },
});

function WidgetSelector({ activeWidget, userWidgetList, vertical, containerStyle }) {
  const [widgetArray, setWidgetArray] = useState(WidgetList);
  const stickyWidget = useSelector((state: DefaultRootState) => state.UI.stickyNoteWidget);
  const translateYValues = useRef(widgetArray.map(() => new Animated.Value(0))).current;
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const tablet = isTablet;
  const landscape = isLandscapeSync();
  
  // Responsive sizing based on device and orientation
  const itemMaxHeight = landscape ? (tablet ? 120 : 100) : (tablet ? 180 : 150);
  const itemMaxWidth = landscape ? (tablet ? 200 : 180) : (tablet ? 300 : 250);
  const itemPadding = landscape ? (tablet ? 12 : 10) : (tablet ? 20 : 16);
  const itemMarginVertical = landscape ? (tablet ? 8 : 6) : (tablet ? 20 : 16);
  const hitSlopSize = tablet ? (landscape ? 15 : 20) : 10;
  // Icon scale factor for tablets - scales down icons while maintaining touch targets
  const iconScale = tablet ? (landscape ? 0.65 : 0.75) : 1;

  const animateElement = (index) => {
    const translateY = translateYValues[index];
    Animated.sequence([
      Animated.timing(translateY, {
        toValue: 100, // Bounce up
        duration: 200,
        easing: Easing.ease,
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 0, // Bounce down
        duration: 600,
        easing: Easing.bounce,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const _handleSelect = (widget, index) => {
    animateElement(index);
    dispatch(changeWidget({ id: widget.id }));
  }

  const renderItem = ({ item, index }) => {
    const translateX = translateYValues[index];
    const isActive = activeWidget.activeWidget.id === item.id;

    return (
      <Animated.View style={[
        {
          left: -13,
          marginVertical: itemMarginVertical,
          maxHeight: itemMaxHeight,
          overflow: 'visible',
          flex: 1,
          transform: [{ translateX }]
        },
        Platform.OS === 'web' && heroStyles.widgetItemContainer
      ]}>
        <Pressable
          hitSlop={tablet ? {
            top: hitSlopSize,
            bottom: hitSlopSize,
            left: hitSlopSize,
            right: hitSlopSize,
          } : undefined}
          onPress={() => {
            _handleSelect(item, index);
          }}
          style={[
            {
              flex: 1,
              minHeight: landscape ? 80 : 100,
            },
            Platform.OS === 'web' && heroStyles.widgetPressable
          ]}
        >
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            {Platform.OS === item?.removeFor[0] ? null : (
              <View style={[
                {
                  flex: 1,
                  maxWidth: itemMaxWidth,
                  padding: Platform.OS === 'web' ? (landscape ? (tablet ? 14 : 12) : (tablet ? 18 : 16)) : itemPadding,
                  minWidth: landscape ? 80 : 100,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: isActive ? (landscape ? (tablet ? 16 : 14) : (tablet ? 20 : 18)) : (Platform.OS === 'web' ? 16 : 0),
                  overflow: 'hidden',
                  position: 'relative',
                },
                Platform.OS === 'web' && [
                  heroStyles.glassCard,
                  isActive && heroStyles.activeGlassCard
                ]
              ]}>
                {/* Gradient Background Layer - Web only */}
                {Platform.OS === 'web' && (
                  <LinearGradient
                    colors={isActive
                      ? ['rgba(255, 165, 0, 0.15)', 'rgba(255, 200, 100, 0.1)', 'rgba(255, 165, 0, 0.1)']
                      : theme
                        ? ['rgba(255, 255, 255, 0.6)', 'rgba(255, 255, 255, 0.4)']
                        : ['rgba(255, 255, 255, 0.1)', 'rgba(255, 255, 255, 0.05)']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={heroStyles.gradientBackground}
                  />
                )}

                {/* Floating Blur Elements - Web only */}
                {Platform.OS === 'web' && isActive && (
                  <>
                    <View style={[heroStyles.floatingBlur, { top: 10, left: 10 }]} />
                    <View style={[heroStyles.floatingBlur, { bottom: 10, right: 10 }]} />
                  </>
                )}

                {/* Blur Overlay for Glass Effect - Web only */}
                {Platform.OS === 'web' && (
                  <View style={heroStyles.blurOverlay} />
                )}

                {/* Original gradient for native */}
                {Platform.OS !== 'web' && (
                  <LinearGradient
                    colors={isActive ? ['orange', 'transparent'] : ['transparent', 'transparent']}
                    style={StyleSheet.absoluteFill}
                  />
                )}

                {/* Content Container */}
                <View style={[
                  {
                    position: 'relative',
                    zIndex: 3,
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: Platform.OS === 'web' ? 0 : 6,
                  },
                  Platform.OS === 'web' && heroStyles.contentContainer
                ]}>
                  {React.cloneElement(
                    item.smallWidget,
                    {},
                    React.cloneElement(
                      React.Children.only(item.smallWidget.props.children),
                      {
                        style: [
                          item.smallWidget.props.children.props.style,
                          {
                            tintColor: (isActive ? undefined : 'rgb(197, 197, 197)'),
                            transform: [{ scale: iconScale }],
                            top: Platform.select({ web: 15 })
                          }
                        ]
                      }
                    )
                  )}
                  <Text style={[
                    {
                      color: theme ? "black" : "white",
                      textAlign: 'center',
                      minHeight: 20,
                      fontWeight: '600',
                      fontSize: landscape ? (tablet ? 12 : 11) : (tablet ? 14 : 13),
                      marginTop: Platform.OS === 'web' ? 0 : 4,
                    },
                    Platform.OS === 'web' && heroStyles.widgetLabel
                  ]}>{item.label}</Text>
                </View>
              </View>
            )}
          </View>
        </Pressable>
      </Animated.View>
    );
  };

  const AddWidget = () => {
    return (
      <Pressable onPress={() => { console.log('add widget selected') }} style={{ flex: 1, maxWidth: 100, paddingHorizontal: 5, overflow: 'visible' }}>
        <View style={styles.smallWidgetContainer}>
          <Text adjustsFontSizeToFit style={{ flex: 1, justifyContent: 'center', textAlign: 'center' }}>Add a Widget</Text>
          <LottieView source={require('@assets/animations/87266-add-to-libraryfile.json')} style={{ height: '100%' }} autoPlay loop={false} />
        </View>
      </Pressable>
    )
  }


  const WidgetListCreator = () => {
    return (

      <FlatList
        data={widgetArray}
        renderItem={renderItem}
        scrollEnabled={true}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[
          {
            paddingVertical: landscape ? (Platform.OS === 'web' ? 8 : 10) : (Platform.OS === 'web' ? 12 : 20),
            alignItems: 'center',
            gap: Platform.select({ web: 12, default: 16 })
          },
          Platform.OS === 'web' && heroStyles.listContent
        ]}
        keyExtractor={(item, index) => item?.id?.toString() || index.toString()}
      />
    )
  }


  return (
    <View style={[
      {
        flex: 1,
        top: landscape ? "-2%" : "-5%",
        overflow: "visible",
        minHeight: landscape ? (tablet ? 300 : 250) : (tablet ? 500 : 400),
        maxHeight: '100%',
        alignSelf: "stretch"
      },
      Platform.OS === 'web' && heroStyles.container,
      containerStyle
    ]}>
      <WidgetListCreator />
    </View>
  )
}

const mapStateToProps = function (state) {
  return {
    activeWidget: state.widgets
  }
}

export default connect(mapStateToProps)(WidgetSelector);